/**
 * Walking a paged listing.
 *
 * Every listing of what an organization or its members create is served a page at a time, by an
 * opaque cursor: the caller names a page size, `limit`, and where to start, `cursor`, under
 * `query`, and each answer names the cursor of the page after it at
 * `extensions.metadata.pagination.next_cursor`, null on the last page. Reading a whole listing is
 * therefore a loop, and the loop is easy to write wrong in ways no request reports: a cursor sent
 * beside other filters than the ones it was issued under is refused, a walk that stops at the
 * first short or empty page drops whatever follows it, and one that follows a cursor it has
 * already sent reads the listing again without end. {@link paginate} is that loop, written once.
 */

import type { PaginationMetadata } from "../../rest/schema.gen.js";
import { MountayaApiError, MountayaConfigurationError, refuseOption } from "./errors.js";
import { abortReasonOf } from "./retry.js";
import type { RequestOptions } from "./transport.js";

/**
 * MAX_PAGE_SIZE is the most rows one page holds: the ceiling the contract sets on every `limit`.
 */
const MAX_PAGE_SIZE = 100;

/**
 * DEFAULT_PAGE_SIZE is the size a walk asks for where the caller names none: the contract's own
 * default for `limit`.
 */
const DEFAULT_PAGE_SIZE = 50;

/** The name a refusal says an option was passed to. */
const CONTEXT = "paginate";

/**
 * PagedResponse is what a paged operation answers with: the rows of one page at `data`, and at
 * `extensions.metadata.pagination` the page size it was served under and the cursor of the page
 * after it.
 *
 * Every Map Studio operation that takes `cursor` and `limit` under `query` answers with this
 * shape, whatever else its metadata carries, so each is an operation {@link paginate} walks.
 */
export interface PagedResponse<T> {
  /** The rows of this page, in the listing's order. */
  readonly data: readonly T[];
  /** The envelope's extensions, where the page names the next one. */
  readonly extensions: {
    /** The page's metadata, which carries more than the cursor on some operations. */
    readonly metadata: {
      /** The page size this page was served under, and the cursor of the next, null on the last. */
      readonly pagination: PaginationMetadata;
    };
  };
}

/**
 * PaginateOptions are the options of one walk: a call's own `retry`, `signal` and `timeoutMs`,
 * which every request of the walk is made under, and the two that shape the walk itself.
 *
 * `pageSize` and `maxPages` are checked when {@link paginate} is called, and a value outside its
 * range throws a `MountayaConfigurationError` naming the option and the value, before any
 * request. `undefined` is an omitted member, never a value.
 */
export interface PaginateOptions extends RequestOptions {
  /**
   * How many rows each request asks for, as a whole number from 1 to 100, the ceiling the API
   * sets on a page. A smaller page is a faster first row and more requests for the whole listing.
   *
   * @default 50
   */
  readonly pageSize?: number | undefined;
  /**
   * The most requests one walk makes, as a whole number of 1 or more, for a caller who wants at
   * most that much of the listing. A walk that reaches it ends there with no error and the rest
   * of the listing unread. Left out, the walk reads to the listing's last page.
   *
   * @default none: the walk ends at the listing's last page.
   */
  readonly maxPages?: number | undefined;
}

/** One page as a walk reads it: its rows, and the cursor of the page after it. */
interface Page<T> {
  readonly next: string | null;
  readonly rows: readonly T[];
}

/**
 * wholeNumberOption reads one numeric option of a walk, refusing a value that is not a whole
 * number from 1 to `max`. Returns `fallback` for an omitted option.
 *
 * @throws `MountayaConfigurationError` naming the option and the value.
 */
const wholeNumberOption = (
  name: "maxPages" | "pageSize",
  value: unknown,
  max: number,
  fallback: number,
): number => {
  if (value === undefined) {
    return fallback;
  }

  if (typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= max) {
    return value;
  }

  const range = Number.isFinite(max) ? `from 1 to ${max}` : "of 1 or more";
  const omitted = Number.isFinite(fallback)
    ? `for the default of ${fallback}`
    : "to walk to the listing's last page";

  throw refuseOption(
    name,
    value,
    `pass a whole number ${range}, or leave it out ${omitted}.`,
    CONTEXT,
  );
};

/**
 * pageOf reads the rows and the next cursor out of one answer.
 *
 * Read member by member, because the transport types a body and does not validate it, and a
 * caller outside the type system can pass an operation that takes no cursor. Such an operation
 * answers the same rows whatever it is sent, so walking it would repeat its one page.
 *
 * @throws `MountayaConfigurationError` for an answer with no `data` list, or no string or null at
 *   `extensions.metadata.pagination.next_cursor`, which is what ends a walk with no `maxPages`.
 */
const pageOf = <T>(answer: PagedResponse<T> | undefined): Page<T> => {
  const rows = answer?.data;
  const next: unknown = answer?.extensions?.metadata?.pagination?.next_cursor;

  if (!Array.isArray(rows) || (typeof next !== "string" && next !== null)) {
    throw new MountayaConfigurationError(
      `${CONTEXT}: the operation answered no page: a paged listing answers a list at data and a cursor or null at extensions.metadata.pagination.next_cursor. Pass an operation that takes cursor and limit under query.`,
    );
  }

  return { next, rows };
};

/**
 * paginate walks a paged Map Studio listing and answers its rows one at a time, requesting each
 * page as the rows before it are read.
 *
 * Every request carries the caller's `args` with the walk's own page under `query`: `limit` is
 * `pageSize`, and `cursor` is where the walk has reached. On the first request that is the
 * caller's own `query.cursor`, where it wrote one, so a walk resumes where an earlier one stopped;
 * after it, the `next_cursor` the latest page named. A `limit` the caller wrote is replaced, and
 * every other member of `args`, the filters and the `sort` under `query` included, is sent as
 * given on every request: a cursor is bound to the filters and the order it was issued under, and
 * the API refuses one sent with others. A page may hold fewer rows than it was asked for, and is
 * followed by its cursor all the same, and so is a page that holds no row and names a cursor: the
 * API cuts a page before it leaves out what the caller may not see, so an empty page can come
 * before the rows that follow it, and only a null `next_cursor` ends a listing.
 *
 * The walk ends, with no further request, at the first of:
 *
 * - a page whose `next_cursor` is null;
 * - `maxPages` requests made, where the caller states it, which leaves the rest of the listing
 *   unread;
 * - a `next_cursor` the walk has already sent, which it refuses to follow: the iteration rejects
 *   with a `MountayaApiError`, since following it would read the listing again without end and
 *   stopping there would answer part of a listing as if it were the whole.
 *
 * An operation's failure rejects the iteration with the error the operation threw, and the rows
 * already answered stay answered.
 *
 * `options.signal` is read before each request, and each request is made under it. Once it
 * aborts, the walk requests no further page: the rows of a page already answered are still
 * answered, and where the walk would have requested the next page it rejects with the signal's
 * own reason, as an aborted call does, so a listing the abort cut short never reads as one read
 * to its end. A walk with no page left to request ends as it would have, with no error.
 *
 * What it answers is iterable more than once, and each iteration walks the listing again from
 * where the caller's own cursor starts it. No request is made until it is iterated, and a reader
 * that stops early (a `break`) leaves the remaining pages unrequested. The API reads by keyset, so
 * a row whose sort key does not change is neither skipped nor read twice, though a row a write
 * moves across the cursor may be.
 *
 * `args` is typed by `operation`, so a member the operation does not declare (a `limit` beside a
 * path parameter, a filter it does not take) is a type error, as it is in a call to the operation
 * itself. `A` is read off the operation's parameter, and `NonNullable<A>` is an inference site of
 * lower priority, which also drops the `undefined` of a parameter declared optional. It decides
 * `A` only where `operation` declares nothing, which is a wrapper whose `args` parameter carries
 * no type: there the arguments type the wrapper, and a member the operation does not declare can
 * pass unrefused. Typing that parameter
 * (`args: Parameters<typeof rest.getOrganizationMembers>[1]`) restores the check.
 *
 * @param operation - A method of the client that takes `cursor` and `limit` under `query`, such
 *   as `mountaya.admin.members.getOrganizationMembers`. An operation that takes no cursor answers
 *   no {@link PagedResponse}, so it is a type error here. A free function takes its transport
 *   first, so it is passed wrapped:
 *   `(args, options) => rest.getOrganizationMembers(transport, args, options)`.
 * @param args - The operation's own arguments: its path parameters, and any filter, `sort` or
 *   starting `cursor` under `query`. `{}` for an operation that requires none.
 * @param options - The walk's `pageSize` and `maxPages`, and the `retry`, `signal` and
 *   `timeoutMs` each request is made under.
 * @returns The listing's rows, in the listing's order, typed as the operation's own.
 * @throws `MountayaConfigurationError` when called, for an `operation` that is not a function,
 *   for `args` or `options` that are not an object, or for a `pageSize` or a `maxPages` outside
 *   its range; and while iterating, for an operation that answers no page, and a
 *   `MountayaApiError` for a `next_cursor` the walk has already sent.
 *
 * @example
 * ```ts
 * const itineraries = [];
 * for await (const itinerary of paginate(mountaya.studio.itineraries.getItineraries, {
 *   query: { organization_id, sort: "-updated_at" },
 * })) {
 *   itineraries.push(itinerary);
 * }
 * ```
 */
export const paginate = <A extends object | undefined, T>(
  operation: (args: A, options?: RequestOptions) => Promise<PagedResponse<T>>,
  args: NonNullable<A>,
  options: PaginateOptions = {},
): AsyncIterable<T> => {
  // All three are used below, and a caller outside the type system can pass each as anything: a
  // method name mistyped there reads `undefined`.
  if (typeof operation !== "function") {
    throw refuseOption(
      "operation",
      operation,
      "pass a method of the client that takes cursor and limit under query, such as mountaya.studio.itineraries.getItineraries.",
      CONTEXT,
    );
  }

  if (typeof args !== "object" || args === null) {
    throw refuseOption(
      "args",
      args,
      "pass the operation's own arguments as an object, {} where it takes none.",
      CONTEXT,
    );
  }

  if (typeof options !== "object" || options === null) {
    throw refuseOption(
      "options",
      options,
      "pass an object such as { pageSize: 25 }, or leave it out for the defaults.",
      CONTEXT,
    );
  }

  const pageSize = wholeNumberOption(
    "pageSize",
    options.pageSize,
    MAX_PAGE_SIZE,
    DEFAULT_PAGE_SIZE,
  );
  const maxPages = wholeNumberOption(
    "maxPages",
    options.maxPages,
    Number.POSITIVE_INFINITY,
    Number.POSITIVE_INFINITY,
  );
  const { retry, signal, timeoutMs } = options;
  // What the caller wrote under `query`, read as the object it is where there is one: its page is
  // replaced below and its other members are sent on. A `query` that is no object, which only a
  // caller outside the type system can pass, holds no member to send on.
  const written: Record<string, unknown> | undefined =
    "query" in args && typeof args.query === "object" && args.query !== null
      ? { ...args.query }
      : undefined;
  const { cursor: start, limit: _replaced, ...query } = written ?? {};

  return {
    async *[Symbol.asyncIterator]() {
      // The caller's own cursor, where it wrote one, starts the walk where an earlier one stopped.
      let cursor: string | undefined = typeof start === "string" ? start : undefined;
      const sent = new Set<string | undefined>();

      for (let requested = 0; requested < maxPages; requested += 1) {
        // Checked here as well as by the request: an operation already answered has nothing left
        // to abort, and the next page is a request the caller has walked away from.
        if (signal?.aborted === true) {
          throw abortReasonOf(signal);
        }

        sent.add(cursor);
        const { next, rows } = pageOf(
          await operation(
            {
              ...args,
              query: { ...query, ...(cursor === undefined ? {} : { cursor }), limit: pageSize },
            },
            { retry, signal, timeoutMs },
          ),
        );

        for (const row of rows) {
          yield row;
        }

        if (next === null) {
          return;
        }

        if (sent.has(next)) {
          throw new MountayaApiError(
            `${CONTEXT}: the listing named the cursor ${JSON.stringify(next)} again, which this walk already followed, so following it would read the listing again without end. Read the listing again from its start.`,
            200,
            [],
          );
        }

        cursor = next;
      }
    },
  };
};
